<script lang="ts">
    import { getContainer } from '$components/Context/ContainerContext.js';
    import ReactionCounter from '$components/Reaction/ReactionCounter.svelte';
    import Spinner from '$components/Spinner/Spinner.svelte';
    import { useFragment } from '$gql/fragment-masking.js';
    import {
        type FeedCommentFragment,
        FeedParticipantFragmentDoc,
        ParticipantAvatarFragmentDoc,
        ParticipantCardFragmentDoc,
    } from '$gql/graphql.js';
    import { cx } from '$lib/utils/cx.js';
    import CommentChildren from './CommentChildren.svelte';
    import FeedCommentEditForm from './FeedCommentEditForm.svelte';
    import FeedCommentForm from './FeedCommentForm.svelte';
    import FeedCommentMenu from './FeedCommentMenu.svelte';
    import FeedCommentReactionWidget from './FeedCommentReactionWidget.svelte';
    import MentionText from './MentionText.svelte';
    import ParticipantAvatar from '../ParticipantAvatar/ParticipantAvatar.svelte';
    import truncate from 'lodash/truncate';
    import { type CommentData } from './Utils';
    import { onMount } from 'svelte';
    import { inview } from 'svelte-inview';
    import ParticipantCard from '$components/ParticipantCard/ParticipantCard.svelte';
    import { fromZonedDateTimeStringToLocalDate } from '$lib/utils/zonedDateTime';
    import * as t from '$trans';
    import { participantToInput } from '$lib/utils/participantUtils.js';

    const { intl, currentUserStore, lang } = getContainer();

    const currentUser = currentUserStore().currentUser();

    const FEED_COMMENT_MAX_LENGTH = 200;
    const FEED_COMMENT_EXTENDED_LENGTH = 40;
    export let comment: FeedCommentFragment;
    export let postId: number;
    export let commentData: CommentData | null = null;

    let inView = false;
    let inEditMode = false;
    let showReplies = true;
    let showReplyModal = false;
    let fullTextMode = false;
    let mustBlinkOnShow = commentData?.commentId == comment.id;
    let loading = false;

    $: author = useFragment(FeedParticipantFragmentDoc, comment.author);

    onMount(() => {
        if (mustBlinkOnShow) {
            setTimeout(() => {
                document
                    .getElementById(`FeedComment:${comment.id}`)
                    ?.scrollIntoView({ behavior: 'smooth' });
            }, 500);
        }
    });

    $: if (comment.body) fullTextMode = false;

    function getText(body: string, showAll: boolean) {
        if (showAll) {
            return body;
        } else {
            if (body.length - FEED_COMMENT_MAX_LENGTH <= FEED_COMMENT_EXTENDED_LENGTH) {
                fullTextMode = false;
                return body;
            } else return truncate(body, { length: FEED_COMMENT_MAX_LENGTH });
        }
    }

    $: if (inView && mustBlinkOnShow) setTimeout(() => (mustBlinkOnShow = false), 1000);

    $: text = getText(comment.body, fullTextMode);
</script>

<div
    use:inview={{}}
    on:inview_change={(event) => {
        const { inView: isInView } = event.detail;
        inView = isInView;
    }}
    id={`FeedComment:${comment.id}`}
>
    <div class="flex items-start">
        <ParticipantCard participant={useFragment(ParticipantCardFragmentDoc, author)}>
            <ParticipantAvatar
                participant={useFragment(ParticipantAvatarFragmentDoc, author)}
                size={comment.hasParent ? 'sm' : 'md'}
            />
        </ParticipantCard>
        <div class="flex-grow w-[200px]">
            <div
                class={cx(
                    'arrow-left arrow-left-t-8 arrow-left-w-10px arrow-left-gray-200 arrow-left-bg-white flex-1 ml-8 bg-white border border-solid border-gray-200 p-4 relative rounded',
                    mustBlinkOnShow && inView ? 'bg-yellow-300' : '',
                )}
            >
                <div>
                    <div class={'my-2 flex gap-x-2 justify-between'}>
                        <div class={'truncate'}>
                            <ParticipantCard
                                participant={useFragment(
                                    ParticipantCardFragmentDoc,
                                    author,
                                )}
                            >
                                <a
                                    href={author.participantProfileLink}
                                    data-sveltekit-reload
                                    class="text-12 font-bold truncate"
                                >
                                    {author.participantName}
                                </a>
                            </ParticipantCard>
                            {#if author.participantTitle != null}
                                <div class={'italic text-12 text-gray-600 truncate'}>
                                    {author.participantTitle}
                                </div>
                            {/if}
                        </div>

                        <div
                            class="italic text-12 text-gray-600 shrink-0 whitespace-no-wrap flex items-center"
                        >
                            <div>
                                {intl().formatDistance(
                                    fromZonedDateTimeStringToLocalDate(
                                        comment.createdDate,
                                    ).toDate(),
                                    new Date(),
                                    {
                                        addSuffix: true,
                                    },
                                )}
                            </div>
                            <FeedCommentMenu
                                {comment}
                                on:edit={() => {
                                    inEditMode = true;
                                }}
                            />
                        </div>
                    </div>

                    {#if !inEditMode}
                        <div class="text-14 break-words font-emoji">
                            <MentionText {text} />

                            {#if comment.body.length > FEED_COMMENT_MAX_LENGTH + FEED_COMMENT_EXTENDED_LENGTH}
                                <button
                                    on:click={() => (fullTextMode = !fullTextMode)}
                                    class={'btn-reset block w-full text-right text-link cursor-pointer hover:text-link-hover hover:underline'}
                                >
                                    {fullTextMode
                                        ? t.feed_post.see_less({}, $lang)
                                        : t.feed_post.see_more({}, $lang)}
                                </button>
                            {/if}
                        </div>
                    {:else}
                        <FeedCommentEditForm
                            commentId={comment.id}
                            body={comment.body}
                            autoFocus
                            onClose={() => (inEditMode = false)}
                            onSubmitSuccess={() => {
                                inEditMode = false;
                            }}
                        />
                        <!--                     Translator.trans("feeds.form.placeholder.feed_comment_body") -->
                        <!-- <CommentInput
                            placeholder={'Votre commentaire'}
                            rows={3}
                            onChange={(value) => setNewContent(value)}
                            value={newContent}
                            required={true}
                            disabled={editing}
                        >
                            <div
                                slot="footer"
                                class={'inline-flex items-center space-x-2'}
                            >
                                {#if loading}
                                    <Spinner size={'sm'} />
                                {/if}

                                <button
                                    disabled={loading}
                                    on:click={() => {
                                        newContent = '';
                                        inEditMode = false;
                                    }}
                                    class={'btn btn-xs btn-gray rounded-full'}
                                >
                                    <i class="fa fa-times" /> Annuler
                                </button>
                                <button
                                    disabled={loading ||
                                        `${newContent || ''}`.trim() === ''}
                                    on:click={() => editFunction()}
                                    class={'btn btn-xs btn-gray rounded-full'}
                                >
                                    <i class="fa fa-check" /> Enregistrer
                                </button>
                            </div>
                        </CommentInput> -->
                    {/if}

                    {#if !inEditMode}
                        <div class="flex items-center justify-between space-x-2 mt-3">
                            <div class={'flex items-center gap-4'}>
                                <FeedCommentReactionWidget {comment} />
                                <ReactionCounter
                                    className={'text-sm'}
                                    counters={comment.reactionsCounters}
                                    showCountOnly
                                />
                                {#if comment.repliesCount > 0}
                                    <button
                                        class={'btn-reset text-link cursor-pointer hover:text-link-hover hover:underline'}
                                        on:click={() => (showReplies = !showReplies)}
                                    >
                                        {t.feed_post.x_replies(
                                            {
                                                nb: comment.repliesCount,
                                            },
                                            $lang,
                                        )}
                                    </button>
                                {/if}

                                {#if !showReplyModal}
                                    <button
                                        disabled={loading}
                                        type={'button'}
                                        on:click={() =>
                                            (showReplyModal = !showReplyModal)}
                                        class={'btn btn-xs btn-gray rounded-full'}
                                    >
                                        {t.feed_post.comment_button_reply({}, $lang)}
                                    </button>
                                {/if}
                            </div>
                            <div class={'flex items-center space-x-2'}>
                                {#if loading}
                                    <Spinner class={'spinner-sm'} />
                                {/if}
                            </div>
                        </div>
                    {/if}

                    <!-- {#if error}
                        <div class={'alert alert-danger mt-2 !py-2'}>{error.message}</div>
                    {/if} -->
                </div>
            </div>
        </div>
    </div>

    {#if showReplyModal && $currentUser != null}
        <div
            class={'my-5'}
            class:ml-16={!comment.hasParent}
            class:ls:ml-24={!comment.hasParent}
        >
            <FeedCommentForm
                {postId}
                initialAuthor={participantToInput($currentUser)}
                initialBody={`@[${author.participantName}](${author.__typename}-${author.id}) `}
                parentId={comment.hasParent ? comment.parent?.id : comment.id}
                onClose={() => (showReplyModal = false)}
                onSubmitSuccess={(commentId) => {
                    showReplyModal = false;
                    setTimeout(() => {
                        document
                            .getElementById(`FeedComment:${commentId}`)
                            ?.scrollIntoView({ behavior: 'smooth' });
                    }, 500);
                }}
                autoFocus
            />
        </div>
    {/if}

    {#if showReplies && !comment.hasParent}
        <div class={'ml-16 ls:ml-24 my-5'}>
            <CommentChildren
                {comment}
                {postId}
                commentData={commentData?.parentId == comment.id ? commentData : null}
            />
        </div>
    {/if}
</div>
